import React from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import type { UnpublishedWebsiteWarningProps } from './UnpublishedWebsiteWarning.types';
import { noop } from 'lodash-es';
import { LazyEmailVerificationDialog } from '@shared/components/EmailVerificationDialog/EmailVerificationDialog.lazy';
import { LazyStationeryCrossSellDialog } from '@shared/components/StationeryCrossSell';

export const UnpublishedWebsiteWarning: React.FC<UnpublishedWebsiteWarningProps> = ({
  isOpen,
  onPublish,
  isPublishing,
  onKeepHidden,
  emailVerificationDialogProps,
  crossSellDialogProps,
}) => {
  const { tq } = useTranslation('websiteDesigner');

  return (
    <>
      {emailVerificationDialogProps.isOpen && <LazyEmailVerificationDialog {...emailVerificationDialogProps} />}
      {crossSellDialogProps.isOpen && <LazyStationeryCrossSellDialog {...crossSellDialogProps} />}
      <ResponsiveDialog
        isOpen={isOpen}
        onClose={noop}
        disableCloseOnEscapeKeyClick
        disableCloseOnOutsideClick
        hideDivider
        hideFooterDivider
        hasCloseButton={false}
        removeBodyPadding
        drawerOverrides={{
          ...drawerOverrides,
          Content: {
            props: {
              ...drawerOverrides.Content?.props,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            },
          },
          Footer: { props: { paddingTop: [7, 7, 7], paddingX: [7, 9, 9], paddingBottom: [7, 9, 9] } },
        }}
        dialogOverrides={{
          Footer: { props: { paddingTop: [6, 7, 7], paddingX: [7, 9, 9], paddingBottom: [7, 9, 9] } },
          Body: { props: { paddingX: [7, 9, 9], paddingTop: [7, 9, 9], paddingBottom: [0, 0, 0] } },
          Content: { props: { width: ['100%', pxToRem(540)] } },
        }}
        renderFooter={() => (
          <Flex
            flexDirection="column"
            alignItems="center"
            width="100%"
            gap={5}
          >
            <ButtonV2
              size="lg"
              maxWidth={pxToRem(380)}
              intent="primary"
              shape="rounded"
              onClick={onPublish}
              width="100%"
              loading={isPublishing}
            >
              {tq`Publish Website`}
            </ButtonV2>
            <ButtonV2
              size="lg"
              maxWidth={pxToRem(380)}
              variant="ghost"
              intent="neutral"
              disabled={isPublishing}
              width="100%"
              onClick={onKeepHidden}
            >
              {tq`Keep the Site Hidden`}
            </ButtonV2>
          </Flex>
        )}
      >
        <TextV2
          typographyVariant="hed6"
          textAlign="center"
          width="100%"
          fontWeight={600}
          marginBottom={4}
        >
          {tq`Publish your site before sharing.`}
        </TextV2>
        <TextV2
          as="p"
          typographyVariant="body2"
          color="mono12"
          textAlign="center"
        >
          {tq`Your website is hidden from guests right now. Publish it so they can view the event details when they receive your message.`}
        </TextV2>
      </ResponsiveDialog>
    </>
  );
};
